
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CourseStickyCTA from "@/features/course/components/course-sticky-cta";
import { resolvePrice } from "@/features/course/components/course-format";

type Props = {
  username?: string | null;
  miniAppUuid: string;
  courseId: number;
  navigateToHref?: string;
  isFree: boolean;
  isPurchased?: boolean;
  isEnrolled?: boolean;
  price?: number;
  specialPrice?: number;
  title?: string;
  lessonsCount?: number;
  usersCount?: number;
  isUnavailable?: boolean;
  autoBuy?: boolean;
}

export default function CoursePurchaseCta(props: Props) {
  const {
    miniAppUuid,
    courseId,
    navigateToHref,
    isFree,
    isPurchased,
    isEnrolled,
    price,
    specialPrice,
    isUnavailable,
    autoBuy,
  } = props;
  const navigate = useNavigate();
  const autoBuyTriggeredRef = useRef(false);

  const shouldRequestAccess = !isPurchased && !isEnrolled;

  const goToPurchase = useCallback(
    (auto = false) => {
      const params = new URLSearchParams({ miniAppUuid });
      if (auto) params.set("auto", "true");
      navigate(`/course/${courseId}/buy?${params.toString()}`, {
        replace: auto,
        state: navigateToHref ? { returnTo: navigateToHref } : undefined,
      });
    },
    [courseId, miniAppUuid, navigateToHref, navigate],
  );

  // Direct-buy links open the purchase page already in auto-buy mode.
  useEffect(() => {
    if (!autoBuy || !shouldRequestAccess || autoBuyTriggeredRef.current) return;
    autoBuyTriggeredRef.current = true;
    goToPurchase(true);
  }, [autoBuy, goToPurchase, shouldRequestAccess]);

  // Already has access — nothing to purchase.
  if (!shouldRequestAccess) {
    return null;
  }

  const { finalPrice, originalPrice, discountPercent } = resolvePrice(
    price ?? 0,
    specialPrice ?? null,
  );

  return (
    <CourseStickyCTA
      isBuying={false}
      isUnavailable={isUnavailable}
      isFreeCourse={isFree}
      finalPrice={finalPrice}
      originalPrice={originalPrice}
      discountPercent={discountPercent}
      onProceed={() => goToPurchase(false)}
    />
  );
}
